import React, { Children, Component, ReactElement } from 'react';
import { createPortal } from 'react-dom';

import { arrayOf, bool, element, oneOfType, string } from 'prop-types';
import { animated } from 'react-spring';
import { Transition } from 'react-spring/renderprops';

import { classNames, getViewportElement } from '@philips-emr/tws-utils';

import { Backdrop } from '..';

import SlideUpPanelContainer from './components/slide-up-panel-container';
import SlideUpPanelContent, { SlideUpPanelContentProps } from './components/slide-up-panel-content';
import SlideUpPanelFooter, { SlideUpPanelFooterProps } from './components/slide-up-panel-footer';
import SlideUpPanelHeader, { SlideUpPanelHeaderProps } from './components/slide-up-panel-header';
import './slide-up-panel.css';

type SlideUpPanelChildren = ReactElement<SlideUpPanelHeaderProps, 'SlideUpPanelHeader'> | ReactElement<SlideUpPanelContentProps, 'SlideUpPanelContent'> | ReactElement<SlideUpPanelFooterProps, 'SlideUpPanelFooter'>;

export interface SlideUpPanelProps {
  children: SlideUpPanelChildren | SlideUpPanelChildren[];
  className?: string;
  closeable?: boolean;
  onClose?: () => void;
  open?: boolean;
}

class SlideUpPanel extends Component<SlideUpPanelProps> {

  static readonly Content = SlideUpPanelContent;
  static readonly Footer = SlideUpPanelFooter;
  static readonly Header = SlideUpPanelHeader;

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]),
    className: string,
    closeable: bool,
    open: bool,
  };

  static defaultProps = {
    closeable: false,
    open: true,
  };

  closePanel = (): void => {
    const { onClose } = this.props;

    onClose?.();
  };

  handleBackdropClick = (): void => {
    const { closeable } = this.props;

    if (closeable) {
      this.closePanel();
    }
  };

  getClassNames(): string {
    const { className } = this.props;

    return classNames({
      'c-slide-up-panel': true,
      [`${className}`]: Boolean(className),
    });
  }

  renderContainer(): ReactElement {
    const { children, closeable, open = true } = this.props;

    const childrenArray = Children.toArray<ReactElement>(children);
    const header = childrenArray.find(child => child.type === SlideUpPanelHeader);
    const content = childrenArray.find(child => child.type === SlideUpPanelContent);
    const footer = childrenArray.find(child => child.type === SlideUpPanelFooter);

    return (
      <SlideUpPanelContainer closeable={closeable} onClose={this.closePanel} open={open}>
        {header}
        {content}
        {footer}
      </SlideUpPanelContainer>
    );
  }

  renderComponent(): ReactElement {
    const { open = true } = this.props;

    return (
      <Transition
        items={open}
        from={{ opacity: 0, transform: 'translate3d(0, 100%, 0)' }}
        enter={{ opacity: 1, transform: 'translate3d(0, 0, 0)' }}
        leave={{ opacity: 0, transform: 'translate3d(0, 100%, 0)' }}
      >
        {open => open && (style => (
          <Backdrop className="c-slide-up-panel__backdrop" onClick={this.handleBackdropClick} style={{ opacity: style.opacity }}>
            <animated.div
              className="c-slide-up-panel"
              onClick={(event) => event.stopPropagation()}
              style={{ transform: style.transform }}
            >
              {this.renderContainer()}
            </animated.div>
          </Backdrop>
        ))}
      </Transition>
    );
  }

  render(): ReactElement {
    return createPortal(this.renderComponent(), getViewportElement());
  }
}

export default SlideUpPanel;
